import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Image } from '~/ui/image';

export type ItineraryItem = {
  title?: string;
  description?: string;
  imageUrl?: string | null;
};

type ItineraryProps = {
  /**
   * Customizable title. Defaults to a localized "Itinerary" heading when
   * omitted or `null` so the section always has a heading.
   */
  title?: ReactNode;
  items: ItineraryItem[];
};

export function Itinerary({ title, items }: ItineraryProps) {
  const resolvedTitle = title ?? (
    <FormattedMessage
      defaultMessage="Itinerary"
      description="Default heading for the itinerary section on the experience details page"
    />
  );
  // The vertical rail (dashed line + dots) only makes sense when there are
  // multiple steps to connect. With a single item it would render an isolated
  // dot and no line, which reads as visual noise rather than an itinerary.
  const showRail = items.length > 1;

  return (
    <Stack as="section" gap="medium">
      <Text as="h2" style="subheadline-large">
        {resolvedTitle}
      </Text>
      <ol className={listStyles}>
        {/*
          Single dashed line behind the rail. Spans from the centre of the
          first item's dot down to the centre of the last item's dot.
          Absolutely positioned so the gap between items doesn't break it.
        */}
        {showRail && <span className={dashedLineStyles} aria-hidden="true" />}

        {items.map((item, i) => (
          <li key={i} className={itemRowStyles}>
            {showRail && (
              <div className={indicatorStyles} aria-hidden="true">
                <span className={dotStyles} />
              </div>
            )}
            <div className={cardStyles}>
              {item.imageUrl && (
                <Image
                  src={item.imageUrl}
                  alt=""
                  width={77}
                  height={77}
                  className={imageStyles}
                />
              )}
              <Stack gap="xsmall" className={css({ flex: '1' })}>
                <Text as="h3" style="body-small-bold" color="secondary">
                  {item.title}
                </Text>
                <Text as="p" style="body-small-regular">
                  {item.description}
                </Text>
              </Stack>
            </div>
          </li>
        ))}
      </ol>
    </Stack>
  );
}

const listStyles = css({
  position: 'relative',
  listStyle: 'none',
  margin: 'none',
  padding: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 'large',
});

const dashedLineStyles = css({
  position: 'absolute',
  left: '[11.5px]',
  top: '[12px]',
  bottom: '[12px]',
  width: '[1px]',
  borderLeft: '[1px dashed]',
  borderColor: 'content.tertiary',
  pointerEvents: 'none',
});

const itemRowStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'medium',
});

const indicatorStyles = css({
  position: 'relative',
  width: '[24px]',
  height: '[24px]',
  flexShrink: 0,
});

const dotStyles = css({
  position: 'absolute',
  left: '[8px]',
  top: '[8px]',
  width: '[8px]',
  height: '[8px]',
  borderRadius: 'full',
  backgroundColor: 'content.tertiary',
});

const cardStyles = css({
  flex: '1',
  minWidth: '[0]',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'medium',
  padding: 'medium',
  border: 'small',
  borderColor: 'border.neutral',
  borderRadius: 'large',
  background: 'surface.raised',
});

const imageStyles = css({
  width: '[77px]',
  height: '[77px]',
  borderRadius: 'small',
  objectFit: 'cover',
  flexShrink: 0,
});
